"use client";

import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";

import { IconMapping } from "@/components/Sidebar/IconMapping";
import {
  isNavItemActive,
  stripNavigationLocalePrefix,
} from "@/lib/navigation/is-nav-item-active";
import { cn } from "@/lib/utils";

const MOBILE_ROW =
  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors";

/**
 * Full-height slide-in drawer for small screens. Sections collapse into
 * accordions (the section holding the current route opens by default) so the
 * long admin/reports menus don't become an unscannable wall of links. Locks
 * body scroll and closes on Escape, backdrop tap, or navigation.
 *
 * It declares itself a modal dialog, so it also has to behave like one: focus
 * moves in on open, stays inside while open, and goes back to whatever opened
 * it on close.
 */
export default function MobileNavMenu({
  open,
  onClose,
  pathname,
  currentHash,
  onNavigateHash,
  sections,
  createLocaleUrl,
}) {
  const [openGroups, setOpenGroups] = useState({});
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  // The parent passes a fresh arrow every render. Read it through a ref so the
  // effect below runs once per open/close instead of on every re-render —
  // re-running it would pull focus back to the close button mid-interaction.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const normalizedPathname = stripNavigationLocalePrefix(pathname || "");
  const isActive = (href) => isNavItemActive(pathname, href, currentHash);
  const sectionContainsActive = (items) =>
    items.some(
      (i) => isActive(i.href) || (i.subItems || []).some((s) => isActive(s.href)),
    );

  // Lock body scroll, close on Escape, and keep Tab inside the panel while the
  // drawer is open. Focus lands on the close button and returns to whatever
  // opened the drawer (the hamburger) once it shuts.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !panelRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [open]);

  // Each time the drawer opens, expand the section that holds the active route.
  // Deliberately keyed on `open` alone: which section starts expanded is
  // decided at open time, and re-running it on every `sections` identity would
  // fight the user's own collapsing.
  useEffect(() => {
    if (!open) return;
    const active = sections.find((s) => sectionContainsActive(s.items));
    setOpenGroups(active ? { [active.label]: true } : {});
  }, [open]);

  if (!open) return null;

  const renderIcon = (iconName) => {
    const Icon = IconMapping[iconName];
    return Icon ? (
      <Icon className="h-[18px] w-[18px] shrink-0 text-gray-400" />
    ) : null;
  };

  const leafClass = (active) =>
    cn(
      MOBILE_ROW,
      active
        ? "bg-brand-50 font-medium text-brand-800"
        : "text-gray-700 hover:bg-brand-50 hover:text-brand-800",
    );

  const renderLeaf = (item) => {
    if (item.display === false || !item.href) return null;
    const content = (
      <>
        {renderIcon(item.iconName)}
        <span className="truncate">{item.label}</span>
      </>
    );
    if (item.external) {
      return (
        <a
          key={item.href || item.label}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={leafClass(false)}
          onClick={onClose}
        >
          {content}
        </a>
      );
    }
    return (
      <Link
        key={item.href || item.label}
        href={item.href}
        className={leafClass(isActive(item.href))}
        onClick={() => {
          const hashIndex = (item.href || "").indexOf("#");
          onNavigateHash(
            hashIndex === -1 ? "" : item.href.substring(hashIndex + 1),
          );
          onClose();
        }}
      >
        {content}
      </Link>
    );
  };

  const renderItem = (item) => {
    if (item.display === false) return null;
    const subs = (item.subItems || []).filter((s) => s.display !== false);
    // A group left with no visible children is not a link — rendering it as one
    // would emit <Link href={undefined}>.
    if (subs.length === 0 && !item.href) return null;
    if (subs.length > 0 && !item.href) {
      return (
        <div key={item.label} className="pt-1">
          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
            {item.label}
          </div>
          <div className="space-y-0.5">{subs.map(renderLeaf)}</div>
        </div>
      );
    }
    if (subs.length > 0 && item.href) {
      return (
        <div key={item.href}>
          {renderLeaf(item)}
          <div className="ml-4 space-y-0.5 border-l border-gray-100 pl-2">
            {subs.map(renderLeaf)}
          </div>
        </div>
      );
    }
    return renderLeaf(item);
  };

  return (
    <div className="lg:hidden">
      <div
        className="fixed inset-0 z-[55] bg-gray-900/30 animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className="fixed inset-y-0 right-0 z-[60] flex w-[86%] max-w-sm flex-col bg-white shadow-xl animate-in slide-in-from-right-8 duration-200"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <span className="text-sm font-semibold tracking-tight text-gray-900">
            Menu
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto overscroll-contain px-2 py-3">
          <Link
            href={createLocaleUrl("/")}
            onClick={onClose}
            className={leafClass(normalizedPathname === "/")}
          >
            {renderIcon("IoHomeOutline")}
            <span>Home</span>
          </Link>

          <div className="mt-2">
            {sections.map((section) => {
              const isOpen = !!openGroups[section.label];
              return (
                <div key={section.label} className="border-t border-gray-100 pt-1">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenGroups((g) => ({
                        ...g,
                        [section.label]: !g[section.label],
                      }))
                    }
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-gray-900 transition-colors hover:bg-gray-50"
                  >
                    <span className="min-w-0">
                      <span className="block">{section.label}</span>
                      {section.subtitle && (
                        <span className="block text-[11px] font-normal text-gray-400">
                          {section.subtitle}
                        </span>
                      )}
                    </span>
                    <ChevronDownIcon
                      className={cn(
                        "h-4 w-4 text-gray-400 transition-transform duration-200",
                        isOpen && "rotate-180",
                      )}
                    />
                  </button>
                  {isOpen && (
                    <div className="space-y-0.5 pb-1 animate-in fade-in slide-in-from-top-1 duration-150">
                      {section.items.map(renderItem)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
